/**
 * The reveal's replay controls (GRA-147): Decision / Back / Forward /
 * Resolution plus a scrubber, over the step model in lib/replayStep.ts.
 *
 * Extracted from LoopShell (GRA-154) so the read-only review of a played
 * Round drives exactly the same controls the reveal does, rather than a
 * lookalike that can drift from them. The component owns no state: the caller
 * holds the `ReplayStep` and applies the pure transitions, which is what lets
 * a review reuse it without inheriting any of the Loop lifecycle around it.
 */

const BUTTON_LOOK =
  "min-h-8 items-center gap-1 rounded border border-muted/30 px-2 py-1 transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0 sm:px-3";
const BUTTON = `flex ${BUTTON_LOOK}`;
/** Decision and Resolution only exist from `sm` up: on a phone the slider's
 *  two ends do the same jump, and the row fits on one line without them. */
const JUMP_BUTTON = `hidden sm:flex ${BUTTON_LOOK}`;

export type ReplayControlsProps = {
  /** Forward candles currently shown: 0 (decision) through `max`. */
  step: number;
  /** Total Forward candles available. */
  max: number;
  /** Where the Round actually resolved, in the same space. */
  resolutionStep: number;
  /**
   * Disables every control without unmounting the row (LoopShell reserves
   * this row's height from the first revealed frame, before the reveal
   * animation has settled into a `ReplayStep` to actually step through —
   * see LoopShell's `showReplayRow`/`canStep` — so a click before that must
   * be inert rather than racing the animation). Defaults to enabled so a
   * caller that doesn't need the distinction (a played-round review, say)
   * is unaffected.
   */
  disabled?: boolean;
  onDecision: () => void;
  onStep: (delta: number) => void;
  onResolution: () => void;
  onScrub: (value: number) => void;
};

export function ReplayControls({
  step,
  max,
  resolutionStep,
  disabled = false,
  onDecision,
  onStep,
  onResolution,
  onScrub,
}: ReplayControlsProps) {
  return (
    <div
      role="group"
      aria-label="step through the reveal"
      // Below `sm` it is a single row, "← slider 6 / 10 →": the button row
      // dissolves (`contents`) so its arrows and counter can sit either side
      // of the slider via `order`. From `sm` it is the stacked desktop layout.
      className="flex items-center gap-2 font-mono text-xs text-muted sm:flex-col sm:gap-4"
    >
      <div className="contents sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-3">
        <button
          type="button"
          onClick={onDecision}
          disabled={disabled || step === 0}
          className={JUMP_BUTTON}
          title="Home"
        >
          Decision
        </button>
        <button
          type="button"
          onClick={() => onStep(-1)}
          disabled={disabled || step === 0}
          className={`${BUTTON} order-1 sm:order-none`}
          // Arrow only below `sm`, so the controls fit one phone row.
          aria-label="Back"
        >
          <span aria-hidden="true">←</span> <span className="hidden sm:inline">Back</span>
        </button>
        <span
          aria-live="polite"
          className="order-3 min-w-[7ch] whitespace-nowrap text-center sm:order-none sm:min-w-0"
        >
          {step} / {max}
        </span>
        <button
          type="button"
          onClick={() => onStep(1)}
          disabled={disabled || step === max}
          className={`${BUTTON} order-4 sm:order-none`}
          aria-label="Forward"
        >
          <span className="hidden sm:inline">Forward</span> <span aria-hidden="true">→</span>
        </button>
        <button
          type="button"
          onClick={onResolution}
          disabled={disabled || step === resolutionStep}
          className={JUMP_BUTTON}
          title="End"
        >
          Resolution
        </button>
      </div>
      {/* A styled native range input rather than a custom widget (spec: keep
          the loop plain), spanning the decision (0) to the fully-revealed end
          of the Forward, the same range the buttons above walk one candle at
          a time. The track goes flat via `appearance-none` here; the thumb's
          own appearance reset and coloring lives in the `.ca-range` rules in
          index.css, since Tailwind's arbitrary `::-webkit-slider-thumb`
          variants get unwieldy once a disabled state is layered on top. */}
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={step}
        disabled={disabled}
        onChange={(event) => onScrub(Number(event.target.value))}
        aria-label="scrub through the reveal"
        className="ca-range order-2 h-1 min-w-0 flex-1 sm:order-none sm:w-full sm:max-w-64 sm:flex-none cursor-pointer appearance-none rounded-full bg-muted/30 accent-accent disabled:cursor-not-allowed disabled:opacity-40"
      />
    </div>
  );
}
